import React, { useState, useEffect } from "react";
import DivTag from "../Components/DivComponent/DivTag";
import ReportHeading from "../Components/ReportHeadings/ReportHeadings";
import { motion } from "framer-motion";
import Seo from "../../components/SeoComponent/Seo";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";
import { MdExpandCircleDown, MdUnfoldMore, MdUnfoldLess } from "react-icons/md";
import { FaPlus, FaMinus } from "react-icons/fa";
import Loader from "../../components/Loader/Loader";
import { apiServiceReportService } from "../../services/apiService";
import { RetriveServicecategoryService } from "../../services/apiaddservice";

const ApiServiceReport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [hiddenColumnData, setHiddenColumnData] = useState(new Set());
  const [isAllExpanded, setIsAllExpanded] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
  });

  const [values, setValues] = useState({
    name: "",
    category_id: "",
  });

  const fetchCategories = async () => {
    try {
      const response = await RetriveServicecategoryService();

      if (response?.success && Array.isArray(response.data)) {
        setCategories(response.data); // Correct
      } else if (response?.status?.code === 200) {
        setCategories(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  //  FIXED fetchData function
  const fetchData = async (page = 1, perPage = null) => {
    setIsLoading(true);
    try {
      const itemsPerPage = perPage !== null ? perPage : pagination.per_page;

      const payload = new FormData();
      if (values.name) payload.append("name", values.name);
      if (values.category_id) payload.append("category_id", values.category_id);

      //  Use "total_data" instead of "per_page"
      payload.append("total_data", String(itemsPerPage));

      const response = await apiServiceReportService(payload, page);

      if (response?.status?.code === 200) {
        const services = response.data || [];
        setData(services);

        setPagination({
          total: response.pagination?.total || services.length,
          per_page: response.pagination?.per_page || itemsPerPage,
          current_page: response.pagination?.current_page || page,
          last_page: response.pagination?.last_page || 1,
        });
      } else {
        showErrorToast("Failed to fetch data");
      }
    } catch (error) {
      showErrorToast("Error fetching data");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Debouncing for Search filter
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (values.name && values.name.length >= 3) {
        // ✅ Pass current per_page
        fetchData(1, pagination.per_page);
      }
      if (values.category_id) {
        // ✅ Pass current per_page
        fetchData(1, pagination.per_page);
      }
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [values.name, values.category_id]); // Don't add pagination.per_page here

  useEffect(() => {
    fetchCategories();
    fetchData();
  }, []);

  const handleChange = (e) => {
    setValues({
      ...values,
      [e.target.name]: e.target.value,
    });
  };

  const handleSearchClick = () => {
    setExpandedRows(new Set());
    // ✅ Pass current per_page
    fetchData(1, pagination.per_page);
  };

  const handleReset = () => {
    // Reset filters
    setValues({
      name: "",
      category_id: "",
    });

    // Reset pagination
    setPagination((prev) => ({
      ...prev,
      per_page: 10, // default value
      current_page: 1,
    }));

    // Reset expanded rows
    setExpandedRows(new Set());

    // Fetch data with default pagination
    fetchData(1, 10);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.last_page) {
      setExpandedRows(new Set());
      //  Pass current per_page explicitly
      fetchData(newPage, pagination.per_page);
    }
  };

  // Toggle column data visibility
  const toggleColumnData = (columnKey) => {
    setHiddenColumnData((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };

  // Check if column data is hidden
  const isDataHidden = (columnKey) => hiddenColumnData.has(columnKey);

  const toggleRowExpansion = (serviceId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId);
      } else {
        newSet.add(serviceId);
      }

      // Update master toggle state
      const rowsWithPackages = data.filter(
        (item) => item.packaged_service?.length > 0,
      );
      setIsAllExpanded(newSet.size === rowsWithPackages.length);
      return newSet;
    });
  };

  // NEW: Master expand/collapse all
  const toggleAllRows = () => {
    if (isAllExpanded) {
      // Collapse all
      setExpandedRows(new Set());
      setIsAllExpanded(false);
    } else {
      // Expand all rows that have packages
      const allRowsWithPackages = data
        .filter(
          (item) => item.packaged_service && item.packaged_service.length > 0,
        )
        .map((item) => item.id);
      setExpandedRows(new Set(allRowsWithPackages));
      setIsAllExpanded(true);
    }
  };

  const getStatusBadge = (status) => {
    const isActive = status?.value?.toLowerCase() === "active";
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${
          isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
        }`}
      >
        {status?.value || "N/A"}
      </span>
    );
  };

  const getPreferenceBadge = (preference) => {
    if (preference === "Highly Recommended") {
      return (
        <span className="px-1 py-0 text-xs font-semibold bg-green-700 text-white rounded">
          Highly_Recommended
        </span>
      );
    }

    if (preference === "Recommended") {
      return (
        <span className="px-1 py-0 text-xs font-semibold bg-green-300 text-green-900 rounded">
          Recommended
        </span>
      );
    }

    return (
      <span className="px-2 py-1 text-xs font-semibold bg-gray-200 text-gray-700 rounded">
        None
      </span>
    );
  };

  const printTable = () => {
    setIsPrinting(true);

    // Expand all rows with packages before printing
    const allRowsWithPackages = data
      .filter(
        (item) => item.packaged_service && item.packaged_service.length > 0,
      )
      .map((item) => item.id);
    setExpandedRows(new Set(allRowsWithPackages));

    setTimeout(() => {
      const printWindow = window.open("", "", "height=800,width=1200");

      const printContents = document.getElementById("reportTable").innerHTML;

      // Write HTML with Tailwind CSS
      printWindow.document.write(
        "<html><head><title>API Service Report</title>",
      );
      printWindow.document.write(
        '<link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">',
      );

      // Add print-specific styles
      printWindow.document.write(`
      <style>
        @media print {
          body { 
            margin: 10px; 
            font-family: Arial, sans-serif;
          }
          table { 
            page-break-inside: auto;
            border-collapse: collapse;
            width: 100%;
          }
          tr { 
            page-break-inside: avoid; 
            page-break-after: auto;
          }
          thead { 
            display: table-header-group;
          }
          .no-print { 
            display: none !important; 
          }
          /* Hide hover effects and interactive elements */
          button { 
            display: none !important; 
          }
        }
        
        /* Report header */
        .print-header {
          text-align: center;
          margin-bottom: 10px;
          border-bottom: 2px solid #1d4ed8;
          padding-bottom: 10px;
        }
        .print-header h1 {
          font-size: 22px;
          font-weight: bold;
          color: #1d4ed8;
          margin-bottom: 5px;
        }
        .print-header p {
          font-size: 12px;
          color: #6b7280;
        }
      </style>
    `);

      printWindow.document.write("</head><body>");

      // Add report header
      printWindow.document.write(`
      <div class="print-header">
        <h1>API Service Report</h1>
        <p>Generated on: ${new Date().toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          dateStyle: "full",
          timeStyle: "short",
        })}</p>
        <p>Total Records: ${pagination.total}</p>
      </div>
    `);

      printWindow.document.write(printContents);
      printWindow.document.write("</body></html>");

      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.close();
        setIsPrinting(false);
        setExpandedRows(new Set()); // Collapse rows after printing
      }, 500);
    }, 100);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.2 }}
    >
      <Seo title="API Service Report" />
      {isLoading && <Loader />}

      <DivTag id="" className="mt-8 bg-white">
        <ReportHeading name="API Service Report" />
        {/* Filter Section */}
        <div className="flex flex-wrap justify-center items-end gap-4 p-4 bg-white rounded-md shadow-sm">
          {/* Service Name */}
          <div className="w-full sm:w-1/3 md:w-1/5 px-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service Name
            </label>
            <input
              type="text"
              name="name"
              value={values.name}
              onChange={handleChange}
              placeholder="Search by name..."
              className="w-full border border-blue-600 rounded-md py-1 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Service Category */}
          <div className="w-full sm:w-1/3 md:w-1/5 px-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service Category
            </label>
            <select
              name="category_id"
              value={values.category_id}
              onChange={handleChange}
              className="w-full border border-blue-600 rounded-md py-1 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Select Category --</option>
              {categories.length > 0 &&
                categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name || "-"}
                  </option>
                ))}
            </select>
          </div>

          {/* Buttons */}
          <div className="w-full sm:w-1/3 md:w-1/6 flex items-center gap-2 px-1 mt-2">
            <button
              type="button"
              onClick={handleSearchClick}
              className="bg-blue-700 text-white rounded-md px-3 py-1 w-full hover:bg-blue-800 transition"
            >
              Search
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="bg-gray-500 text-white rounded-md px-3 py-1 w-full hover:bg-gray-600 transition"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Place above your table */}
        <div className="flex items-center justify-between my-1 px-2">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Show:</label>
            <select
              value={pagination.per_page}
              onChange={(e) => {
                const perPage = Number(e.target.value);

                setPagination((prev) => ({
                  ...prev,
                  per_page: perPage,
                  current_page: 1,
                }));

                setExpandedRows(new Set());
                fetchData(1, perPage); // This will send total_data=50
              }}
              className="border border-blue-600 px-3 py-1 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {[10, 20, 30, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-700">entries</span>
          </div>

          <div className="text-sm text-gray-700">
            Total Records:{" "}
            <span className="font-semibold">{pagination.total}</span>
          </div>
          <button
            onClick={printTable}
            disabled={isPrinting || data.length === 0}
            className="flex items-center gap-1 bg-green-600 text-white px-2 py-0 rounded hover:bg-green-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z"
                clipRule="evenodd"
              />
            </svg>
            {isPrinting ? "Printing..." : "Print Report"}
          </button>
        </div>

        <DivTag className="overflow-x-auto p-2">
          <div className="overflow-x-auto">
            <table className="w-full" id="reportTable">
              <thead className="bg-blue-700 w-full text-white">
                <tr className="w-full">
                  {/* SN Column Header */}
                  {!isDataHidden("sn") && (
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleColumnData("sn")}
                          className="text-yellow-300 hover:text-yellow-400 transition-colors"
                          title={
                            isDataHidden("sn") ? "Show SN data" : "Hide SN data"
                          }
                        >
                          {isDataHidden("sn") ? (
                            <FaPlus className="text-lg" />
                          ) : (
                            <FaMinus className="text-lg" />
                          )}
                        </button>
                        <span>SN</span>
                      </div>
                    </th>
                  )}

                  {/* Service Category Column Header */}
                  {!isDataHidden("category") && (
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleColumnData("category")}
                          className="text-yellow-300 hover:text-yellow-400 transition-colors"
                          title={
                            isDataHidden("category")
                              ? "Show category data"
                              : "Hide category data"
                          }
                        >
                          {isDataHidden("category") ? (
                            <FaPlus className="text-lg" />
                          ) : (
                            <FaMinus className="text-lg" />
                          )}
                        </button>
                        <span>Service Category</span>
                      </div>
                    </th>
                  )}
                  {/* Service Name Column Header */}
                  {!isDataHidden("name") && (
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleColumnData("name")}
                          className="text-yellow-300 hover:text-yellow-400 transition-colors"
                          title={
                            isDataHidden("name")
                              ? "Show name data"
                              : "Hide name data"
                          }
                        >
                          {isDataHidden("name") ? (
                            <FaPlus className="text-lg" />
                          ) : (
                            <FaMinus className="text-lg" />
                          )}
                        </button>
                        <span>Service Name</span>
                      </div>
                    </th>
                  )}

                  {/* Service Name Column Header */}
                  {!isDataHidden("vendorName") && (
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleColumnData("vendorName")}
                          className="text-yellow-300 hover:text-yellow-400 transition-colors"
                          title={
                            isDataHidden("vendorName")
                              ? "Show vendorName data"
                              : "Hide vendorName data"
                          }
                        >
                          {isDataHidden("vendorName") ? (
                            <FaPlus className="text-lg" />
                          ) : (
                            <FaMinus className="text-lg" />
                          )}
                        </button>
                        <span>Vendor Name</span>
                      </div>
                    </th>
                  )}

                  {/* Activation Fee Column Header */}
                  {!isDataHidden("fee") && (
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleColumnData("fee")}
                          className="text-yellow-300 hover:text-yellow-400 transition-colors"
                          title={
                            isDataHidden("fee")
                              ? "Show fee data"
                              : "Hide fee data"
                          }
                        >
                          {isDataHidden("fee") ? (
                            <FaPlus className="text-lg" />
                          ) : (
                            <FaMinus className="text-lg" />
                          )}
                        </button>
                        <span>Activation Fee</span>
                      </div>
                    </th>
                  )}
                  {/* Endpoint Column Header */}
                  {!isDataHidden("endpoint") && (
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleColumnData("endpoint")}
                          className="text-yellow-300 hover:text-yellow-400 transition-colors"
                          title={
                            isDataHidden("endpoint")
                              ? "Show endpoint data"
                              : "Hide endpoint data"
                          }
                        >
                          {isDataHidden("endpoint") ? (
                            <FaPlus className="text-lg" />
                          ) : (
                            <FaMinus className="text-lg" />
                          )}
                        </button>
                        <span>Endpoint</span>
                      </div>
                    </th>
                  )}

                  {/* Preference Column Header */}
                  {!isDataHidden("preference") && (
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleColumnData("preference")}
                          className="text-yellow-300 hover:text-yellow-400 transition-colors"
                          title={
                            isDataHidden("preference")
                              ? "Show preference data"
                              : "Hide preference data"
                          }
                        >
                          {isDataHidden("preference") ? (
                            <FaPlus className="text-lg" />
                          ) : (
                            <FaMinus className="text-lg" />
                          )}
                        </button>
                        <span>Preference</span>
                      </div>
                    </th>
                  )}

                  {/* Status Column Header */}
                  {!isDataHidden("status") && (
                    <th className="px-3 py-2 text-center text-xs font-semibold uppercase">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => toggleColumnData("status")}
                          className="text-yellow-300 hover:text-yellow-400 transition-colors"
                          title={
                            isDataHidden("status")
                              ? "Show status data"
                              : "Hide status data"
                          }
                        >
                          {isDataHidden("status") ? (
                            <FaPlus className="text-lg" />
                          ) : (
                            <FaMinus className="text-lg" />
                          )}
                        </button>
                        <span>Status</span>
                      </div>
                    </th>
                  )}

                  {/* Packages Column Header */}
                  {!isDataHidden("packages") && (
                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => toggleColumnData("packages")}
                          className="text-yellow-300 hover:text-yellow-400 transition-colors"
                          title={
                            isDataHidden("packages")
                              ? "Show packages data"
                              : "Hide packages data"
                          }
                        >
                          {isDataHidden("packages") ? (
                            <FaPlus className="text-lg" />
                          ) : (
                            <FaMinus className="text-lg" />
                          )}
                        </button>
                        <span>Packages</span>
                      </div>
                    </th>
                  )}

                  <th>
                    {data.some((item) => item.packaged_service?.length > 0) && (
                      <button
                        onClick={toggleAllRows}
                        className="text-white hover:text-yellow-300 transition-colors duration-200 flex items-center gap-1 text-sm ml-2"
                        title={isAllExpanded ? "Collapse All" : "Expand All"}
                      >
                        {isAllExpanded ? (
                          <>
                            <MdExpandCircleDown className="rotate-180 text-lg" />
                            <span className="text-xs">All</span>
                          </>
                        ) : (
                          <>
                            <MdExpandCircleDown className="text-lg" />
                            <span className="text-xs">All</span>
                          </>
                        )}
                      </button>
                    )}
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {data.length > 0 ? (
                  data.map((item, index) => (
                    <React.Fragment key={item.id}>
                      <tr
                        className={`${
                          index % 2 === 0
                            ? "bg-gray-50"
                            : "bg-gray-200 border-2"
                        } hover:bg-blue-50 w-fit transition-colors duration-150`}
                      >
                        {/* SN Cell */}
                        {!isDataHidden("sn") && (
                          <td className="px-2 py-1 text-sm text-gray-900 font-medium border-r border-black">
                            {isDataHidden("sn") ? (
                              <span className="text-gray-300">—</span>
                            ) : (
                              item.id
                            )}
                          </td>
                        )}

                        {/* Service Category Cell */}
                        {!isDataHidden("category") && (
                          <td className="px-2 py-1 text-xs text-gray-700 border-r border-black">
                            {isDataHidden("category") ? (
                              <span className="text-gray-300">—</span>
                            ) : (
                              item.service_category?.name || "N/A"
                            )}
                          </td>
                        )}

                        {/* Service Name Cell */}
                        {!isDataHidden("name") && (
                          <td className="px-2 py-1 text-xs text-black font-bold border-r border-black">
                            {isDataHidden("name") ? (
                              <span className="text-gray-300">—</span>
                            ) : (
                              item.service_name
                            )}
                          </td>
                        )}

                        {!isDataHidden("vendorName") && (
                          <td className="px-2 py-1 text-xs text-gray-900 font-medium border-r border-black">
                            {isDataHidden("vendorName") ? (
                              <span className="text-gray-300">—</span>
                            ) : (
                              item.vendor?.name || "N/A"
                            )}
                          </td>
                        )}

                        {/* Activation Fee Cell */}

                        {!isDataHidden("fee") && (
                          <td className="px-3 py-2 text-xs font-semibold text-gray-800 whitespace-nowrap border-r border-black">
                            {isDataHidden("fee") ? (
                              <span className="text-gray-300">—</span>
                            ) : item?.activation_fee ? (
                              <span className="inline-flex items-center gap-1 rounded-md bg-green-50 px-2 py-0.5 text-green-700">
                                <span className="text-[10px]">₹</span>
                                {Number(item.activation_fee).toLocaleString(
                                  "en-IN",
                                )}
                              </span>
                            ) : (
                              <span className="text-gray-400 italic">N/A</span>
                            )}
                          </td>
                        )}

                        {/* Endpoint Cell */}
                        {!isDataHidden("endpoint") && (
                          <td className="px-2 py-0 text-xs text-gray-600 font-semibold border-r border-black">
                            {isDataHidden("endpoint") ? (
                              <span className="text-gray-300">—</span>
                            ) : (
                              item.end_point
                            )}
                          </td>
                        )}
                        {/* Preference Cell */}
                        {!isDataHidden("preference") && (
                          <td className="px-2 py-1 border-r border-black">
                            {isDataHidden("preference") ? (
                              <span className="text-gray-300">—</span>
                            ) : (
                              getPreferenceBadge(item.preference)
                            )}
                          </td>
                        )}

                        {/* Status Cell */}
                        {!isDataHidden("status") && (
                          <td className="px-2 py-1 text-center border-r border-black">
                            {isDataHidden("status") ? (
                              <span className="text-gray-300">—</span>
                            ) : (
                              getStatusBadge(item.status)
                            )}
                          </td>
                        )}

                        {/* Packages Cell */}
                        {!isDataHidden("") && (
                          <td className="px-2 py-1 text-sm text-gray-900 text-right font-medium border-r border-black">
                            {isDataHidden("packages") ? (
                              <span className="text-gray-300">—</span>
                            ) : item.packaged_service &&
                              item.packaged_service.length > 0 ? (
                              <button
                                onClick={() => toggleRowExpansion(item.id)}
                                className="ml-2 text-blue-600 hover:text-blue-800 font-bold text-lg transition-transform duration-200"
                                aria-label={
                                  expandedRows.has(item.id)
                                    ? "Collapse"
                                    : "Expand"
                                }
                              >
                                <MdExpandCircleDown
                                  className={`transition-transform duration-200 ${
                                    expandedRows.has(item.id)
                                      ? "rotate-180"
                                      : ""
                                  }`}
                                />
                              </button>
                            ) : (
                              <span className="text-gray-400 ml-2">—</span>
                            )}
                          </td>
                        )}

                        {expandedRows.has(item.id) &&
                          !isDataHidden("packages") && (
                            <td
                              colSpan={8}
                              className="px-0 py-0 border-2 rounded-md"
                            >
                              <div className="text-xs text-gray-800 p-2">
                                {item.packaged_service.map((pkg) => (
                                  <div key={pkg.id}>
                                    {pkg.package?.name || "N/A"} –{" "}
                                    {pkg.service_charge?.amount
                                      ? `₹${pkg.service_charge.amount.toFixed(2)}`
                                      : "N/A"}{" "}
                                    ({pkg.service_charge?.charge_type || "N/A"})
                                  </div>
                                ))}
                              </div>
                            </td>
                          )}
                      </tr>
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data.length > 0 && (
            <div className="mt-3 flex justify-between items-center">
              <div className="text-sm text-gray-700">
                Showing{" "}
                {(pagination.current_page - 1) * pagination.per_page + 1} to{" "}
                {Math.min(
                  pagination.current_page * pagination.per_page,
                  pagination.total,
                )}{" "}
                of {pagination.total} entries
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                  className="px-3 py-1 bg-blue-600 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-700 transition"
                >
                  Previous
                </button>
                <span className="px-3 py-1 bg-gray-100 rounded">
                  Page {pagination.current_page} of {pagination.last_page}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.last_page}
                  className="px-3 py-1 bg-blue-600 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-700 transition"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </DivTag>

        <ToastContainer />
      </DivTag>
    </motion.div>
  );
};

export default ApiServiceReport;
